import { Fragment, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from "react";
import { useGlobalFunction } from "../../Context/GlobalFunctionContext";
import { isValidArray } from "../../Layout/Common/common";
import { SubFamilyProductSkeleton } from "../../Skeleton/subFamilyProductSkeleton";
import { FamilyProductRow } from "./subfamily/familyProductRow";
import { FamilyProductList } from "./subfamily/familyProductList";
import { FilterConatiner } from "./family/filterContainer";
import { excludeAttributeColumns } from "../../constants";

const DESKTOP_QUERY = "(min-width: 992px)";
const desktopMedia = typeof window !== "undefined" && window.matchMedia ? window.matchMedia(DESKTOP_QUERY) : null;

const subscribeDesktop = (onChange) => {
  if (!desktopMedia) return () => {};
  desktopMedia.addEventListener("change", onChange);
  return () => desktopMedia.removeEventListener("change", onChange);
};

export const useIsDesktopView = () =>
  useSyncExternalStore(
    subscribeDesktop,
    () => (desktopMedia ? desktopMedia.matches : true),
    () => true,
  );

const buildFeatureFromRow = (item) => {
  if (!item || !item.leftAttributeId || !item.feature) return [];
  try {
    const jsonArray = JSON.parse(item.feature);
    const idList = item.leftAttributeId.split(",").map((num) => parseInt(num, 10));
    return jsonArray
      .filter((attr) => idList.includes(attr.Attr_Id))
      .sort((a, b) => idList.indexOf(a.Attr_Id) - idList.indexOf(b.Attr_Id))
      .map((attr) => ({ attributeName: attr.DisplayName, attributeValue: attr.Attr_Value }));
  } catch (e) {
    console.error("render feature for subfamily " + e);
    return [];
  }
};

const isExcludedColumn = (name) =>
  excludeAttributeColumns.some((excluded) => excluded.trim().toLowerCase() === String(name).trim().toLowerCase());

const collectAttributeColumns = (rows) => {
  const columns = [];
  rows.forEach(({ feature }) =>
    feature.forEach(({ attributeName }) => {
      if (!attributeName || isExcludedColumn(attributeName)) return;
      if (!columns.includes(attributeName)) columns.push(attributeName);
    }),
  );
  return columns;
};

const useStickyColumnOffsets = (layoutKey) => {
  const wrapperRef = useRef(null);
  const tableRef = useRef(null);
  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    const table = tableRef.current;
    if (!wrapper || !table || typeof ResizeObserver === "undefined") return;
    const headerCell = (name) => table.querySelector(`thead th.sticky-${name}`);
    const update = () => {
      const imgWidth = headerCell("img")?.offsetWidth ?? 0;
      const infoWidth = headerCell("info")?.offsetWidth ?? 0;
      const buyWidth = headerCell("buy")?.offsetWidth ?? 0;
      table.style.setProperty("--sticky-code-left", `${imgWidth}px`);
      table.style.setProperty("--sticky-info-right", `${buyWidth}px`);
      table.style.setProperty("--sticky-cost-right", `${buyWidth + infoWidth}px`);
      wrapper.classList.toggle("is-scrollable", wrapper.scrollWidth > wrapper.clientWidth + 1);
    };
    let frame = 0;
    const scheduleUpdate = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };
    const observer = new ResizeObserver(scheduleUpdate);
    observer.observe(wrapper);
    observer.observe(table);
    table.querySelectorAll("thead th.sticky-col").forEach((cell) => observer.observe(cell));
    update();
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [layoutKey]);
  return { wrapperRef, tableRef };
};

export const ProductTable = ({ rows, limit = null }) => {
  const attributeColumns = isValidArray(rows) ? collectAttributeColumns(rows) : [];
  const visibleCount = limit ? Math.min(limit, rows?.length ?? 0) : (rows?.length ?? 0);
  const { wrapperRef, tableRef } = useStickyColumnOffsets(`${visibleCount}|${attributeColumns.join("|")}`);

  if (!isValidArray(rows)) return <Fragment>No Products</Fragment>;

  const visibleRows = limit ? rows.slice(0, limit) : rows;

  return (
    <div className="product-table-scroll" ref={wrapperRef}>
      <table
        ref={tableRef}
        className="table basic-table display product-table fluid-width nowrap orderlist-table"
        style={{ width: "100%" }}
      >
        <thead>
          <tr>
            <th className="sticky-col sticky-img" />
            <th className="sticky-col sticky-code">Code</th>
            {attributeColumns.map((name) => (
              <th key={name}>{name}</th>
            ))}
            <th className="sticky-col sticky-cost">Cost</th>
            <th className="dataTable-status sticky-col sticky-buy text-start">Buy</th>
          </tr>
        </thead>
        <tbody>
          {visibleRows.map(({ item, feature }) => (
            <FamilyProductRow key={item.productId} row={item} feature={feature} attributeColumns={attributeColumns} />
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const ProductViewToggle = ({ view, onChange }) => (
  <div className="sort-filters">
    <ul className="d-none d-lg-flex card-row nav" role="tablist">
      <li className="nav-item" role="presentation">
        <a
          href="#tableView"
          className={`grid-products${view === "table" ? " active" : ""}`}
          role="tab"
          aria-selected={view === "table"}
          onClick={(event) => {
            event.preventDefault();
            onChange("table");
          }}
        >
          <span className="wes-row-filter" />
        </a>
      </li>
      <li className="nav-item" role="presentation">
        <a
          href="#gridView"
          className={`row-products${view === "grid" ? " active" : ""}`}
          role="tab"
          aria-selected={view === "grid"}
          onClick={(event) => {
            event.preventDefault();
            onChange("grid");
          }}
        >
          <span className="wes-card-filter" />
        </a>
      </li>
    </ul>
  </div>
);

export const SubFamilyContainer = ({ params, view = "table" }) => {
  const [isVisible, setIsVisible] = useState(false);
  const divRef = useRef(null);

  useEffect(() => {
    setIsVisible(false);
    const node = divRef.current;
    if (!node || !params) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [params]);

  return (
    <div ref={divRef} className="col-lg-12" id={`subfamily-${params?.familyId ?? "unknown"}`}>
      {params && (
        <Fragment>
          <div className="proSection-innerTitle">
            <h5>{params.familyName}</h5>
          </div>
          <div className="subfamily-products">
            <p>{params.descriptions}</p>
            {isVisible ? (
              <FamilyProductContainer params={params} view={view} />
            ) : (
              <div className="row">
                <div className="col-sm-12 col-md-12 col-lg-12 mt-3 mt-lg-4">
                  <SubFamilyProductSkeleton count={2} />
                </div>
              </div>
            )}
          </div>
        </Fragment>
      )}
    </div>
  );
};

export const FamilyProductContainer = ({ params, view = "table" }) => {
  const [filter, setFilter] = useState(null);
  const [productList, setProductList] = useState(null);
  const [rawFilterData, setRawFilterData] = useState(null);
  const [prodAttrFilter, setProdAttrFilter] = useState(null);
  const [prodFilteredData, setProdFilteredData] = useState(null);
  const [productFiltered, setProductFiltered] = useState(null);
  const [visibleCount, setVisibleCount] = useState(50);
  const { getFamilyFilter, getFamilyProducts } = useGlobalFunction();

  useEffect(() => {
    if (params && params.familyId) {
      fetchFamilyFilter(params.familyId);
      fetchFamilyProduct(params.familyId);
    }
    return () => {
      setFilter(null);
      setProductList(null);
      setRawFilterData(null);
      setProdAttrFilter(null);
      setProdFilteredData(null);
      setProductFiltered(null);
      setVisibleCount(50);
    };
  }, [params]);

  const fetchFamilyFilter = async (fid) => {
    const response = await getFamilyFilter(fid);
    if (response) {
      setRawFilterData(response);
      var t = sendFilterAttr(response);
      const sortedData = t.sort((a, b) => a.attributeName.localeCompare(b.attributeName));
      setProdAttrFilter(sortedData);
      setProdFilteredData(sortedData);
      setFilter(response);
    } else {
      setProdAttrFilter(null);
      setProdFilteredData(null);
      setFilter(null);
    }
  };

  const fetchFamilyProduct = async (fid) => {
    const response = await getFamilyProducts(fid);
    if (isValidArray(response)) {
      setProductList(response);
      setProductFiltered(response);
    } else {
      setProductList([]);
      setProductFiltered([]);
    }
  };

  const sendFilterAttr = (data) => {
    if (data) {
      const groupedData = data.reduce((acc, item) => {
        const { attributeName, attributeValue, disabled } = item;
        const group = acc.find((entry) => entry.attributeName == attributeName);
        if (!group) {
          acc.push({
            attributeName,
            attributeValues: [
              {
                name: attributeName,
                value: attributeValue,
                selected: false,
                disabled: disabled,
                count: 1,
              },
            ],
          });
        } else {
          const nameEntry = group.attributeValues.find((entry) => entry.value == attributeValue);

          if (!nameEntry) {
            group.attributeValues.push({
              name: attributeName,
              value: attributeValue,
              selected: false,
              disabled: disabled,
              count: 1,
            });
          } else {
            nameEntry.count++;
          }
        }

        return acc;
      }, []);
      return groupedData;
    }
  };

  const handleModifyData = (filter) => {
    const transformed = Object.entries(filter).map(([key, value]) => ({
      name: key,
      value,
    }));
    if (!isValidArray(transformed)) {
      setProductFiltered(productList);
      setProdFilteredData(prodAttrFilter);
      return;
    }
    handleAttrFilterSelected(transformed);
  };

  const handleAttrFilterSelected = (p) => {
    if (isValidArray(p)) {
      var Original = rawFilterData;
      var temp = [];
      var cnt = p.length;
      var i = 0;
      var mainAttr = sendFilterAttr(Original);
      var totalCount = parseInt(mainAttr.length);
      if (cnt == 0) {
        temp = mainAttr;
      } else {
        temp = [...temp, ...mainAttr.filter((item) => item.attributeName == p[0].name)];
      }
      var FilteredAttrList = [];
      let filterproductids;
      while (i < cnt) {
        var filteredData = Original.filter(
          (item) => p[i].value.includes(item.attributeValue) && item.attributeName == p[i].name,
        );
        filterproductids = [...new Set(filteredData.map((entry) => entry.productId))];
        Original = rawFilterData.filter((entry) => filterproductids.includes(entry.productId));

        var temp3 = rawFilterData.map((itemA) => {
          const matchingItem = Original.find(
            (itemB) => itemB.attributeName == itemA.attributeName && itemB.attributeValue == itemA.attributeValue,
          );
          if (!matchingItem) {
            return { ...itemA, disabled: 1 };
          } else {
            return { ...itemA };
          }
        });
        FilteredAttrList = sendFilterAttr(temp3);
        if (i < parseInt(cnt - 1)) {
          temp = [...temp, ...FilteredAttrList.filter((item) => item.attributeName == p[i + 1].name)];
        }
        i = parseInt(i) + 1;
      }
      i = parseInt(temp.length);

      var filteredProductList = productList.filter((entry) => filterproductids.includes(parseInt(entry.productId)));
      setProductFiltered(filteredProductList);

      if (i < totalCount) {
        const missedData = FilteredAttrList.filter(
          (itemA) => !temp.some((itemB) => itemB.attributeName == itemA.attributeName),
        );
        temp = [...temp, ...missedData];
      }
      const sortedData = temp.sort((a, b) => a.attributeName.localeCompare(b.attributeName));

      setTimeout(() => {
        setProdFilteredData(sortedData);
      }, 500);
    }
  };

  const rows = isValidArray(productFiltered)
    ? productFiltered.map((item) => ({ item, feature: buildFeatureFromRow(item) }))
    : [];

  return (
    <Fragment>
      {productList ? (
        isValidArray(productList) ? (
          <div className="row mt-3 productsFilters">
            {isValidArray(prodFilteredData) && rawFilterData && (
              <FilterConatiner
                params={prodFilteredData}
                filteredData={prodFilteredData}
                handleFilterEvent={(e) => {
                  handleModifyData(e);
                }}
              />
            )}
            {productFiltered ? (
              <div className="col-sm-12 col-md-12 col-lg-12 mt-3 mt-lg-4">
                {isValidArray(productFiltered) && (
                  <Fragment>
                    {view === "table" ? (
                      <ProductTable rows={rows} limit={visibleCount} />
                    ) : (
                      rows
                        .slice(0, visibleCount)
                        .map(({ item, feature }) => (
                          <FamilyProductList key={item.productId} row={item} feature={feature} />
                        ))
                    )}
                    {visibleCount < productFiltered.length && (
                      <div className="filterbtn-left justify-content-center mt-3 mt-lg-3">
                        <button className="btn" onClick={() => setVisibleCount((prev) => prev + 50)}>
                          Load More ({`${visibleCount}/${productFiltered.length}`})
                        </button>
                        {/* <button className="btn btn-danger danger" onClick={() => setVisibleCount((prev) => prev + 50)}>
                          Load More ({`${visibleCount}/${productFiltered.length}`})
                        </button> */}
                      </div>
                    )}
                  </Fragment>
                )}
              </div>
            ) : (
              <SubFamilyProductSkeleton count={3} />
            )}
          </div>
        ) : (
          <div className="row mt-3" style={{ display: "none" }}>
            <div className="col-sm-12 col-md-12 col-lg-12 mt-3 mt-lg-4">
              <div className="no-data-found">No Products Found </div>
            </div>
          </div>
        )
      ) : (
        <div className="row mt-3">
          <div className="col-sm-12 col-md-12 col-lg-12 mt-3 mt-lg-4">
            <div className="no-data-found">
              <SubFamilyProductSkeleton count={3} />
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
};
